import React, { useState, useEffect, useRef } from 'react';
import LoginPage from './LoginPage';
import LandingPage from './LandingPage';
import DashboardPage from './DashboardPage';
import VBPPage from './VBPPage';
import InitiativesPage from './InitiativesPage';
import ContractsPage from './ContractsPage';
import UsersPage from './UsersPage';
import ChangePasswordModal from './ChangePasswordModal';
import { TOKEN_KEY, apiFetch } from './api';

const AUTH_KEY = 'eh_authed';
const USER_KEY = 'eh_user';

const PATH_TO_SCREEN = {
  '/dashboard':   'dashboard',
  '/initiatives': 'initiatives',
  '/vbp':         'vbp',
  '/contracts':   'contracts',
  '/users':       'users',
};

function screenFromURL() {
  if (!sessionStorage.getItem(AUTH_KEY) || !sessionStorage.getItem(TOKEN_KEY)) return 'login';
  return PATH_TO_SCREEN[window.location.pathname] ?? 'home';
}

function storedUser() {
  try { return JSON.parse(sessionStorage.getItem(USER_KEY)); } catch { return null; }
}

export default function App() {
  const [screen, setScreen]           = useState(screenFromURL);
  const [currentUser, setCurrentUser] = useState(storedUser);
  const [showCpw, setShowCpw]         = useState(false);

  useEffect(() => {
    const handler = () => setScreen(screenFromURL());
    window.addEventListener('popstate', handler);
    return () => window.removeEventListener('popstate', handler);
  }, []);

  useEffect(() => {
    const handler = () => navigate('login');
    window.addEventListener('auth-expired', handler);
    return () => window.removeEventListener('auth-expired', handler);
  }, []);

  const navigate = (next) => {
    if (next === 'login') {
      sessionStorage.removeItem(AUTH_KEY);
      sessionStorage.removeItem(USER_KEY);
      sessionStorage.removeItem(TOKEN_KEY);
      setCurrentUser(null);
      history.pushState(null, '', '/');
      setScreen('login');
      return;
    }
    sessionStorage.setItem(AUTH_KEY, '1');
    const paths = { home: '/', dashboard: '/dashboard', initiatives: '/initiatives', vbp: '/vbp', contracts: '/contracts', users: '/users' };
    history.pushState({ screen: next }, '', paths[next] ?? '/');
    setScreen(next);
  };

  const handleLogin = (user, token) => {
    sessionStorage.setItem(TOKEN_KEY, token);
    sessionStorage.setItem(USER_KEY, JSON.stringify(user));
    setCurrentUser(user);
    if (user.mustChangePassword) {
      sessionStorage.setItem(AUTH_KEY, '1');
      history.pushState(null, '', '/');
      setScreen('home');
      setShowCpw(true);
    } else {
      navigate('home');
    }
  };

  const handlePasswordChanged = () => {
    setShowCpw(false);
    const updated = { ...currentUser, mustChangePassword: false };
    sessionStorage.setItem(USER_KEY, JSON.stringify(updated));
    setCurrentUser(updated);
  };

  const handleUserUpdated = () => {
    apiFetch('/api/users').then(r => r.json()).then(users => {
      const fresh = users.find(u => u.email === currentUser.email);
      if (fresh) {
        const updated = { ...currentUser, name: fresh.name };
        sessionStorage.setItem(USER_KEY, JSON.stringify(updated));
        setCurrentUser(updated);
      }
    }).catch(() => {});
  };

  const chatStateRef = useRef({ messages: [], sessionId: null, messagesRemaining: 5, sessionExpired: false, showHistory: false });

  const commonProps = { onNavigate: navigate, currentUser, onLogout: () => navigate('login'), onUserUpdated: handleUserUpdated };

  if (screen === 'login') return <LoginPage onLogin={handleLogin} />;

  return (
    <>
      {screen === 'dashboard'   && <DashboardPage   {...commonProps} chatStateRef={chatStateRef} />}
      {screen === 'initiatives' && <InitiativesPage  {...commonProps} />}
      {screen === 'vbp'         && <VBPPage          {...commonProps} chatStateRef={chatStateRef} />}
      {screen === 'contracts' && <ContractsPage  {...commonProps} chatStateRef={chatStateRef} />}
      {screen === 'users'     && <UsersPage      {...commonProps} />}
      {screen === 'home'      && <LandingPage    {...commonProps} />}

      {showCpw && currentUser && (
        <ChangePasswordModal
          userEmail={currentUser.email}
          required={true}
          onSuccess={handlePasswordChanged}
          onClose={() => {}}
        />
      )}
    </>
  );
}
